// File: frontend/src/utils/markupUtils.tsx

import { RiExternalLinkLine } from "react-icons/ri";
import React from "react";
import { SpoilerBlock } from "../components/entries/SpoilerBlock";
import { renderBasicMarkup } from "./inlineMarkup";

// ─── Regex patterns for entry markup syntax ───────────────────────────────────
const REF_NEW    = /\(ref:\s*(.*?)\|\|(\d+)\)/g;
const REF_LEGACY = /\((ref|bkz):\s*([^\s)]+)\)/g;
const BOLD       = /\(b:\s*(.*?)\)/g;
const ITALIC     = /\(i:\s*(.*?)\)/g;
const LINK       = /\(link:\s*([^|)]+)\|([^)]+)\)/g;
const ENTRY_REF  = /\(#(\d+)\)/g;
// Entity reference tokens inserted by the entry editor toolbar's ref popups.
// These mirror the patterns in EntryRow.tsx's collectSpans() — they were
// missing here, which is why they showed up as raw, unclickable text in the
// admin edit modal's preview instead of resolving to a real link.
const MAC_REF    = /\(mac:\s*(.*?)\|\|(\d+)\)/g;                          // Match ref → /mac--{matchId}
const PERSON_REF = /\(kisi:\s*(.*?)\|\|([^)]+)\)/g;                       // Person ref → /kisi/{slug}
const CLUB_REF   = /\(kulup:\s*(.*?)\|\|([^)]+)\)/g;                      // Club ref → /kulup/{slug}
const TEAM_REF   = /\(takim:\s*(.*?)\|\|([^)\s]+)\)/g;            // Team ref → /takim/{tail}; tail is "{id}" (current) or "{clubSlug}/{teamSlug}" (legacy)
const TOPIC_REF  = /\(baslik:\s*(.*?)\|\|([^)]+)\)/g;                     // Custom topic ref → /gundem/{slug}
// Spoiler block: (spoiler: text) — collapsed by default, click to reveal.
// Quote block: (alinti: text) — pasted via the toolbar's alıntı modal.
// Both are located with findBlockToken() below instead of a single regex —
// see that function's comment for why a regex can't safely do this.
// "(bkz: ...)" reference variants — inserted by the ref popups' bkz insert mode.
// Same "||tail" semantics as the inline tokens; rendered as "(bkz: <link>)".
const BKZ_REFS: Array<{ pattern: RegExp; keyPrefix: string; buildHref: (tail: string) => string; titlePrefix: string }> = [
  { pattern: /\(bkz-mac:\s*(.*?)\|\|(\d+)\)/g, keyPrefix: "bkz-mac", buildHref: (tail) => `/mac--${tail}`, titlePrefix: "maça git" },
  { pattern: /\(bkz-kisi:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kisi", buildHref: (tail) => `/kisi/${tail}`, titlePrefix: "kişiye git" },
  { pattern: /\(bkz-kulup:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-kulup", buildHref: (tail) => `/kulup/${tail}`, titlePrefix: "kulübe git" },
  { pattern: /\(bkz-takim:\s*(.*?)\|\|([^)\s]+)\)/g, keyPrefix: "bkz-takim", buildHref: (tail) => `/takim/${tail}`, titlePrefix: "takıma git" },
  { pattern: /\(bkz-baslik:\s*(.*?)\|\|([^)]+)\)/g, keyPrefix: "bkz-baslik", buildHref: (tail) => `/gundem/${tail}`, titlePrefix: "başlığa git" },
];

/**
 * Result of a single block-token match found by findBlockToken().
 */
export interface BlockTokenMatch {
  /** Start index of the full "(keyword: ...)" match within the source text. */
  index: number;
  /** Length of the full match, including the wrapping "(" and ")". */
  length: number;
  /** Raw text between "(keyword:" and its matching closing ")". */
  content: string;
}

/**
 * Find the next "(spoiler: ...)" or "(alinti: ...)" block in `text`,
 * searching from `fromIndex` onward, by walking parenthesis depth instead
 * of relying on a single regex.
 *
 * Why this exists: the previous regex approach —
 * `\(spoiler:\s*((?:[^()]|\([^()]*\))*)\)` — only tolerates exactly one
 * level of nested parentheses inside the block's text. When a user pastes
 * content with two or more nested parentheses, or with a stray/unbalanced
 * "(" or ")", that regex closes the block at the first available ")"
 * instead of the real one, silently splitting the rest of the pasted text
 * out as plain text after the block. Counting depth character by character
 * finds the true matching closing parenthesis no matter how deeply the
 * content is nested, as long as the content's own parentheses are balanced.
 *
 * Returns null once no further "(keyword:" opening tag exists in the text.
 */
export function findBlockToken(
  text: string,
  keyword: "spoiler" | "alinti",
  fromIndex: number
): BlockTokenMatch | null {
  const openTag = `(${keyword}:`;
  let searchFrom = fromIndex;

  // Loop so an unbalanced opening tag (a stray "(" inside the pasted text
  // that never closes) doesn't hide a later, valid block of the same kind.
  while (true) {
    const openIndex = text.indexOf(openTag, searchFrom);
    if (openIndex === -1) return null;

    // Content starts right after "(keyword:", skipping leading whitespace.
    let contentStart = openIndex + openTag.length;
    while (contentStart < text.length && /\s/.test(text[contentStart])) contentStart++;

    // depth starts at 1 — we are already inside the block's opening "(".
    // The matching close is the position where depth returns to 0.
    let depth = 1;
    let i = contentStart;
    for (; i < text.length; i++) {
      const ch = text[i];
      if (ch === "(") depth++;
      else if (ch === ")") {
        depth--;
        if (depth === 0) break;
      }
    }

    if (depth === 0) {
      return { index: openIndex, length: i + 1 - openIndex, content: text.slice(contentStart, i) };
    }

    // No balanced close found — keep looking past this opening tag.
    searchFrom = openIndex + openTag.length;
  }
}

/**
 * Replace every "(keyword: ...)" block in `text` using `replacer`, walking
 * parenthesis depth via findBlockToken() so content with nested or
 * unbalanced parentheses is handled correctly.
 */
function replaceBlockTokens(
  text: string,
  keyword: "spoiler" | "alinti",
  replacer: (content: string) => string
): string {
  let result = "";
  let cursor = 0;
  let block = findBlockToken(text, keyword, cursor);
  while (block) {
    result += text.slice(cursor, block.index) + replacer(block.content);
    cursor = block.index + block.length;
    block = findBlockToken(text, keyword, cursor);
  }
  result += text.slice(cursor);
  return result;
}

/**
 * Strip all markup tags from raw entry/message content.
 * Returns plain text for previews, snippets, and conversation list rows.
 */
export function stripMarkup(text: string): string {
  // Handle spoiler/alinti blocks first — their content can contain "(" / ")"
  // characters that the simpler regex replacements below can't cope with.
  text = replaceBlockTokens(text, "spoiler", () => "(spoiler)");
  text = replaceBlockTokens(text, "alinti", (content) => content.trim());

  return text
    .replace(/\(bkz-(?:mac|kisi|kulup|takim|baslik):\s*(.*?)\|\|[^)]*\)/g, (_, label) => label.trim())
    .replace(/\(ref:\s*(.*?)\|\|\d+\)/g, (_, label) => label.trim())
    .replace(/\(link:\s*[^|)]+\|([^)]+)\)/g, (_, display) => display.trim())
    .replace(/\((b|i):\s*(.*?)\)/g, (_full, _tag, inner) => inner)
    .replace(/\((bkz|ref):\s*[^\s)]+\)/g, "");
}

/**
 * Strip markup then truncate to maxLen characters for narrow list previews.
 */
export function buildSnippet(raw: string, maxLen = 120): string {
  const plain = stripMarkup(raw);
  if (plain.length <= maxLen) return plain;
  return plain.slice(0, maxLen).trimEnd() + "…";
}

interface Span {
  index: number;
  length: number;
  node: React.ReactNode;
}

/**
 * Render entry markup syntax as React nodes for inline previews.
 * Handles bold, italic, refs, links, and entry references.
 */
export function renderEntryMarkup(text: string): React.ReactNode[] {
  const spans: Span[] = [];

  REF_NEW.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = REF_NEW.exec(text)) !== null;) {
    const label = m[1].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <span key={`ref-${m.index}`} className="epf-preview-ref">{label}</span>,
    });
  }

  REF_LEGACY.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = REF_LEGACY.exec(text)) !== null;) {
    if (spans.some((s) => s.index === m!.index)) continue;
    const label = m[2].replace(/--\d+$/, "").replace(/-/g, " ");
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <span key={`legacy-${m.index}`} className="epf-preview-ref">{label}</span>,
    });
  }

  BOLD.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = BOLD.exec(text)) !== null;) {
    spans.push({ index: m.index, length: m[0].length, node: <strong key={`b-${m.index}`}>{m[1]}</strong> });
  }

  ITALIC.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = ITALIC.exec(text)) !== null;) {
    spans.push({ index: m.index, length: m[0].length, node: <em key={`i-${m.index}`}>{m[1]}</em> });
  }

  LINK.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = LINK.exec(text)) !== null;) {
    const url = m[1].trim();
    const display = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <span key={`link-${m.index}`} className="epf-preview-link" title={url}>
          {display} <RiExternalLinkLine className="epf-preview-link-arrow" size={12} aria-hidden="true" />
        </span>
      ),
    });
  }

  ENTRY_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = ENTRY_REF.exec(text)) !== null;) {
    spans.push({
      index: m.index,
      length: m[0].length,
      node: <span key={`eref-${m.index}`} className="epf-preview-entry-ref">#{m[1]}</span>,
    });
  }

  // --- mac: match reference (mac: label||matchId) — links to /mac--{matchId} ---
  MAC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = MAC_REF.exec(text)) !== null;) {
    const label = m[1].trim();
    const matchId = m[2];
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <a key={`mac-${m.index}`} href={`/mac--${matchId}`} target="_blank" rel="noopener noreferrer"
          className="epf-preview-entity-link" title={`maça git: ${label}`}>
          {label}
        </a>
      ),
    });
  }

  // --- kisi: person reference (kisi: displayName||slug) — links to /kisi/{slug} ---
  PERSON_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = PERSON_REF.exec(text)) !== null;) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <a key={`kisi-${m.index}`} href={`/kisi/${slug}`} target="_blank" rel="noopener noreferrer"
          className="epf-preview-entity-link" title={`kişiye git: ${label}`}>
          {label}
        </a>
      ),
    });
  }

  // --- kulup: club reference (kulup: displayName||slug) — links to /kulup/{slug} ---
  CLUB_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = CLUB_REF.exec(text)) !== null;) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <a key={`kulup-${m.index}`} href={`/kulup/${slug}`} target="_blank" rel="noopener noreferrer"
          className="epf-preview-entity-link" title={`kulübe git: ${label}`}>
          {label}
        </a>
      ),
    });
  }

  // --- takim: team reference (takim: label||id) — links to /takim/{tail}.
  //     Tails written before the id-only form still carry "clubSlug/teamSlug"
  //     and keep resolving through the two-segment route. ---
  TEAM_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = TEAM_REF.exec(text)) !== null;) {
    const label = m[1].trim();
    const slugPath = m[2].trim(); // "{id}", or legacy "clubSlug/teamSlug"
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <a key={`takim-${m.index}`} href={`/takim/${slugPath}`} target="_blank" rel="noopener noreferrer"
          className="epf-preview-entity-link" title={`takıma git: ${label}`}>
          {label}
        </a>
      ),
    });
  }

  // --- baslik: custom topic reference (baslik: title||slug) — links to /gundem/{slug} ---
  TOPIC_REF.lastIndex = 0;
  // eslint-disable-next-line no-loop-func
  for (let m: RegExpExecArray | null; (m = TOPIC_REF.exec(text)) !== null;) {
    const label = m[1].trim();
    const slug = m[2].trim();
    spans.push({
      index: m.index,
      length: m[0].length,
      node: (
        <a key={`baslik-${m.index}`} href={`/gundem/${slug}`} target="_blank" rel="noopener noreferrer"
          className="epf-preview-entity-link" title={`başlığa git: ${label}`}>
          {label}
        </a>
      ),
    });
  }

  // --- spoiler: collapsed block — hidden until the reader clicks the toggle ---
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "spoiler", 0); block; block = findBlockToken(text, "spoiler", block.index + block.length)) {
    const hidden = block.content.trim();
    spans.push({
      index: block.index,
      length: block.length,
      node: <SpoilerBlock key={`spoiler-${block.index}`} text={hidden} />,
    });
  }

  // --- alinti: quote block — faded background, italic, wrapped in quotes ---
  // eslint-disable-next-line no-loop-func
  for (let block = findBlockToken(text, "alinti", 0); block; block = findBlockToken(text, "alinti", block.index + block.length)) {
    const quoted = block.content.trim();
    spans.push({
      index: block.index,
      length: block.length,
      node: (
        <blockquote key={`alinti-${block.index}`} className="entry-quote-block">
          {"\u201C"}{renderBasicMarkup(quoted)}{"\u201D"}
        </blockquote>
      ),
    });
  }

  // --- bkz-* variants: rendered as a parenthesized "(bkz: <link>)" note ---
  for (const v of BKZ_REFS) {
    v.pattern.lastIndex = 0;
    // eslint-disable-next-line no-loop-func
    for (let m: RegExpExecArray | null; (m = v.pattern.exec(text)) !== null;) {
      const label = m[1].trim();
      const tail = m[2].trim();
      spans.push({
        index: m.index,
        length: m[0].length,
        node: (
          <span key={`${v.keyPrefix}-${m.index}`} className="epf-preview-bkz">
            {"(bkz: "}
            <a href={v.buildHref(tail)} target="_blank" rel="noopener noreferrer"
              className="epf-preview-entity-link" title={`${v.titlePrefix}: ${label}`}>
              {label}
            </a>
            {")"}
          </span>
        ),
      });
    }
  }

  spans.sort((a, b) => a.index - b.index);

  // Drop spans fully contained inside an earlier (outer) span — e.g. a (b: ...)
  // token pasted inside an (alinti: ...) quote must not render a second time.
  const filtered: Span[] = [];
  let coveredUntil = -1;
  for (const s of spans) {
    if (s.index < coveredUntil) continue;
    filtered.push(s);
    coveredUntil = s.index + s.length;
  }

  const nodes: React.ReactNode[] = [];
  let last = 0;
  for (const s of filtered) {
    if (s.index > last) nodes.push(text.slice(last, s.index));
    nodes.push(s.node);
    last = s.index + s.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
}